import { useCallback, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ListStocksUseCase } from "../../../Usecase/Stocks/list-stocks-usecase";
import { IListSearchPayload } from "../../../Components/List/list.types";

const DEFAULT_PRODUCT_IMAGE =
  'https://acdn.mitiendanube.com/stores/002/907/105/themes/common/logo-1336738559-1706047471-a90c2b04f7208c4f190adf866d8df0b51706047472-320-0.webp';

interface IUseListStocksProps {
  listStocksUseCase: ListStocksUseCase
}

export function useListStocks({ listStocksUseCase }: IUseListStocksProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [page, setPage] = useState(1)
    const [data, setData] = useState<any[]>([])
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [currentSearch, setCurrentSearch] = useState('')

    const { showToast } = useToast()

    const listStocks = useCallback(async ({ page: currentPage, searchBy = '' }: IListSearchPayload) => {
      try {
        setLoading(true)
        setLoadError(false)

        const listResult = await listStocksUseCase.handle({
          page: currentPage,
          search: searchBy,
        });

        if (listResult.isFailure) {
          setLoadError(true)
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        const list = listResult.getValue()

        if (list) {
          setData(list.data ?? [])
          setTotalRegisters(list.total ?? list.data?.length ?? 0)
          setLoadError(false)
        }

        setPage(currentPage)
        setCurrentSearch(searchBy ?? '')
      } catch (error) {
        setLoadError(true)
        console.error("error listStocks function: ", error)
      } finally {
        setLoading(false);
      }
    }, [listStocksUseCase, showToast]);

    const handleSearch = useCallback((payload: IListSearchPayload) => {
      listStocks(payload)
    }, [listStocks]);

    const handlePageChange = useCallback((payload: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      listStocks({
        page: payload.page,
        searchBy: payload.searchBy ?? currentSearch,
      })
    }, [listStocks, currentSearch]);

    const list = useMemo(() => {
        return data?.map((product) => ({
            id: product?.productId,
            internalName: product?.productInternalName,
            name: product?.productName,
            allocatedStockQuantity: product?.allocatedStockQuantity ?? 0,
            workInProgressQuantity: product?.workInProgressQuantity ?? 0,
            finishedGoodsQuantity: product?.finishedGoodsQuantity ?? 0,
            photo: product?.productImages?.[0]?.link || DEFAULT_PRODUCT_IMAGE,
        })) ?? []
    }, [data])

    return {
        loading,
        loadError,
        list,
        totalRegisters,
        page,
        currentSearch,
        handleSearch,
        handlePageChange,
        listStocks,
    }
}
